import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, Wallet, CheckCircle2 } from "lucide-react";
import { submitAudit } from "../lib/api";
import Seo from "../components/Seo";
import Header from "../components/Header";
import Footer from "../components/Footer";

const empty = {
  nom: "", email: "", telephone: "", entreprise: "",
  heures_contenu: "", budget_mensuel: "", plateformes_nb: "",
  frequence: "", ventes_actuelles: "", message: "",
};

const numify = (f) => ({
  ...f,
  heures_contenu: Number(f.heures_contenu) || 0,
  budget_mensuel: Number(f.budget_mensuel) || 0,
  plateformes_nb: Number(f.plateformes_nb) || 0,
  frequence: Number(f.frequence) || 0,
  ventes_actuelles: Number(f.ventes_actuelles) || 0,
});

export default function Audit() {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setStatus("loading");
    try {
      const res = await submitAudit(numify(form));
      setPreview(res.preview); setStatus("ok"); setForm(empty);
    } catch { setStatus("err"); }
  };

  return (
    <div>
      <Seo routeKey="/pilote" path="/audit" title="Bilan de situation gratuit | VIRALYA" description="Un bilan de situation 100 % gratuit : vos problèmes, les solutions et les bénéfices chiffrés en temps et en argent. Sans carte, sans appel de vente déguisé." />
      <Header />
      <main className="page">
        <div className="container page-narrow">
          <Link to="/" className="back-link"><ArrowLeft size={15} /> Accueil</Link>
          <span className="eyebrow"><span className="sq" />Bilan de situation · Gratuit</span>
          <h1 className="h2" style={{ marginBottom: "0.5rem" }}>Recevez votre bilan de situation chiffré</h1>
          <p className="lead" style={{ marginBottom: "1.5rem" }}>100 % gratuit. À partir de vos réponses, nous établissons un bilan personnalisé : vos problèmes, les solutions et le gain estimé en temps et en argent.</p>

          <div className="glass card" style={{ borderLeft: "4px solid var(--v-accent)", marginBottom: "1.5rem" }}>
            <p style={{ margin: 0 }}><strong>Gratuit, sans carte, sans appel de vente déguisé.</strong> On analyse votre situation, on chiffre ce qui est jouable pour vous, et si le pilote n'est pas pour vous, on vous le dit.</p>
          </div>

          {status === "ok" && preview ? (
            <div className="glass card" data-testid="audit-success">
              <span className="eyebrow"><span className="sq" />Aperçu de votre bilan</span>
              <div className="bilan-preview">
                <div className="bilan-metric">
                  <Clock size={20} className="bilan-ico" />
                  <div className="bilan-v tnum">{preview.temps_recupere_mois}<span className="bilan-u"> h/mois</span></div>
                  <div className="bilan-l">temps récupéré (estimé)</div>
                </div>
                <div className="bilan-metric">
                  <Wallet size={20} className="bilan-ico" />
                  <div className="bilan-v tnum">{preview.benefice_total_mois}<span className="bilan-u"> CHF/mois</span></div>
                  <div className="bilan-l">bénéfice mensuel estimé</div>
                </div>
              </div>
              <p style={{ marginTop: "1.25rem" }}><strong>Votre bilan complet vous sera envoyé sous 48 h ouvrées</strong> : le détail de vos problèmes, les solutions et les bénéfices chiffrés, solution par solution.</p>
              <p style={{ marginTop: "0.5rem", fontSize: "0.82rem", color: "var(--text-secondary)" }}>Estimation indicative établie à partir de vos réponses. Aucun engagement.</p>
            </div>
          ) : (
            <form className="glass card" onSubmit={submit} data-testid="audit-form">
              <h3 className="h3" style={{ marginBottom: "0.25rem", fontSize: "1.05rem" }}>Vos coordonnées</h3>
              <div className="form-grid" style={{ marginBottom: "1.5rem" }}>
                <div className="field"><label>Nom complet</label><input required value={form.nom} onChange={set("nom")} data-testid="audit-nom" /></div>
                <div className="field"><label>E-mail</label><input required type="email" value={form.email} onChange={set("email")} data-testid="audit-email" /></div>
                <div className="field"><label>Téléphone</label><input value={form.telephone} onChange={set("telephone")} data-testid="audit-tel" /></div>
                <div className="field"><label>Entreprise (optionnel)</label><input value={form.entreprise} onChange={set("entreprise")} data-testid="audit-entreprise" /></div>
              </div>

              <h3 className="h3" style={{ marginBottom: "0.25rem", fontSize: "1.05rem" }}>Votre situation</h3>
              <div className="form-grid">
                <div className="field"><label>Heures/semaine passées à créer & publier du contenu</label><input type="number" min="0" step="0.5" value={form.heures_contenu} onChange={set("heures_contenu")} data-testid="audit-heures" /></div>
                <div className="field"><label>Budget mensuel actuel (agence, CM, freelance) en CHF</label><input type="number" min="0" step="10" value={form.budget_mensuel} onChange={set("budget_mensuel")} data-testid="audit-budget" /></div>
                <div className="field"><label>Nombre de plateformes gérées</label><input type="number" min="0" step="1" value={form.plateformes_nb} onChange={set("plateformes_nb")} data-testid="audit-plateformes" /></div>
                <div className="field"><label>Fréquence de publication souhaitée (par semaine)</label><input type="number" min="0" step="1" value={form.frequence} onChange={set("frequence")} data-testid="audit-frequence" /></div>
                <div className="field"><label>Leads ou ventes actuels par mois (optionnel)</label><input type="number" min="0" step="1" value={form.ventes_actuelles} onChange={set("ventes_actuelles")} data-testid="audit-ventes" /></div>
                <div className="field full"><label>Ce que vous cherchez à régler (optionnel)</label><textarea rows={3} value={form.message} onChange={set("message")} data-testid="audit-message" /></div>
              </div>
              <button className="btn btn--primary" style={{ marginTop: "1.25rem" }} disabled={status === "loading"} data-testid="audit-submit">
                {status === "loading" ? "Analyse…" : "Recevoir mon bilan gratuit"}
              </button>
              {status === "err" && <p className="form-status err">Un problème est survenu. Merci de réessayer.</p>}
              <p style={{ marginTop: "1rem", display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                <CheckCircle2 size={14} /> Données traitées selon notre <Link to="/legal/confidentialite" className="af-link">politique de confidentialité (LPD)</Link>.
              </p>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
